// src/components/chat/MessageList.jsx
import React from "react";
import { Box, VStack, Text, Image } from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import DOMPurify from "dompurify";
import { markdownToHtml, transformHtml } from "../../helpers/markdownHelpers";

import ItinerayCard from "./ItinerayCard";
import Activities from "./Activities";
import HotelsCard from "./HotelsCard";
import FlightCard from "./FlightCard";
import RVRentalCard from "./RVRentalCard";
import Translate from "../../components/LanguageTranslation/Translate";
import ExpediaSearchWidget from "./ExpediaSearchWidget";

/* typing-dots animation */
const typingKeyframes = keyframes`
  0% { content:'' }
  33%{ content:'.' }
  66%{ content:'..' }
 100%{ content:'...' }
`;

/* ────────────────  tiny helper: show car-hire widget inside an iframe ───────────── */
const CarHireIframe = ({
  locale = "en-GB",
  market = "UK",
  currency = "GBP",
  height = 500, // increased height for better widget display
}) => {
  const srcDoc = `<!doctype html>
<html lang="${locale}">
<head>
  <meta charset="utf-8"/>
  <script src="https://widgets.skyscanner.net/widget-server/js/loader.js" async></script>
  <style>
    html,body{margin:0;padding:0;height:100%;font-family:Arial,sans-serif;overflow:hidden;}
    .bucket-section{margin-top:20px;text-align:center;padding:10px;}
    .bucket-divider{width:100%;height:1px;background:#E2E8F0;margin:15px 0;}
    .bucket-text{font-size:12px;color:#666;margin-bottom:10px;}
    .bucket-button{background:#286328;color:white;border:none;padding:8px 16px;border-radius:8px;font-size:12px;cursor:pointer;transition:background 0.2s;}
    .bucket-button:hover{background:#1F4D1F;}
    #skyscanner-widget{height:100%;overflow:hidden;}
  </style>
</head>
<body>
  <div data-skyscanner-widget="CarHireWidget"
       data-locale="${locale}"
       data-market="${market}"
       data-currency="${currency}">
  </div>
  <div class="bucket-section">
    <div class="bucket-divider"></div>
    <div class="bucket-text">Did you find what you were looking for? Add them to your bucket list.</div>
    <button class="bucket-button" onclick="window.parent.location.href='/ai-travel-planner/bucket-list?fromChat=true'">Go to Bucket List</button>
  </div>
</body>
</html>`;

  return (
    <Box
      w="100%"
      h={`${height}px`}
      borderRadius="xl"
      overflow="hidden"
      bg="#F6F6F6"
       px={3}
    >
      <iframe
        title="Car-hire widget"
        style={{ 
          width: "100%", 
          height: "100%", 
          border: "none",
          overflow: "hidden"
        }}
        srcDoc={srcDoc}
        scrolling="no"
      />
    </Box>
  );
};

/* ───────────────────────── helper that decides what to render ─────────────────── */
const renderBotContent = (bot, idx, onSelectItem, conversationId) => {
  if (!bot) return null;
  const { type, message, category } = bot;
  // console.log("bot", bot);
  switch (type) {
    case "trip_plan":
      return (
        <ItinerayCard
          key={`trip-${idx}`}
          tripData={message}
          onSelectItem={onSelectItem}
        />
      );

    case "flight_information":
      return (
        Array.isArray(message) && (
          <FlightCard
            key={`f-${idx}`}
            data={message}
            category={category || []}
            conversationId={conversationId}
          />
        )
      );

    case "rv_information":
      return (
        <RVRentalCard
          key={`rv-${idx}`}
          RVs={message || []}
          location={message?.location}
          category={category || []}
          conversationId={conversationId}
        />
      );

    case "accommodation_information":
      return (
        <HotelsCard
          key={`h-${idx}`}
          hotels={message || []}
          location={message?.location}
          category={category || []}
          conversationId={conversationId}
        />
      );

    case "activities_information":
      return (
        <Activities
          key={`a-${idx}`}
          data={Array.isArray(message) ? message : []}
          location={message?.location}
          category={category || []}
          conversationId={conversationId}
        />
      );

    /* ───────────── default branch: plain text OR the car-hire snippet ─────────── */
    default: {
      /* Detect the Skyscanner snippet in either short-token or full-HTML form */
      const widgetRegex = /data-skyscanner-widget\s*=\s*["']CarHireWidget["']/i;
      if (message === "[CAR_HIRE_WIDGET]" || widgetRegex.test(message)) {
        // If it's just the token, render only the widget
        if (message === "[CAR_HIRE_WIDGET]") {
          return <CarHireIframe key={`w-${idx}`} />;
        }
        
        // If it contains both text and widget, split them
        const widgetMatch = message.match(widgetRegex);
        if (widgetMatch) {
          // Find the complete widget HTML block (div + script)
          const completeWidgetRegex = /<div[^>]*data-skyscanner-widget[^>]*>.*?<\/div>\s*<script[^>]*src=["']https:\/\/widgets\.skyscanner\.net[^"']*["'][^>]*><\/script>/is;
          const completeWidgetMatch = message.match(completeWidgetRegex);
          
          if (completeWidgetMatch) {
            const widgetStart = message.indexOf(completeWidgetMatch[0]);
            const beforeWidget = message.substring(0, widgetStart).trim();
            const widgetEnd = widgetStart + completeWidgetMatch[0].length;
            const afterWidget = message.substring(widgetEnd).trim();
            
            return (
              <Box
                key={`combined-${idx}`}
                maxW={{ base: "90%", md: "65%", lg: "65%" }}
                borderRadius="xl"
                bg="#F6F6F6"
                overflow="hidden"
              >
                {/* Render text before the widget, if any */}
                {beforeWidget && (
                  <Box p={3} pb={beforeWidget && afterWidget ? 2 : 3}>
                    <Text
                      color="black"
                      mb={2}
                      sx={{
                        a: { color: "blue.500", textDecoration: "underline" },
                        p: {
                          whiteSpace: "pre-line",
                          marginBottom: "12px",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        },
                        "p:last-child": { marginBottom: 0 },
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          transformHtml(markdownToHtml(beforeWidget)),
                          { ADD_ATTR: ["target", "rel"] }
                        ),
                      }}
                    />
                  </Box>
                )}
                {/* Render the car hire widget */}
                <CarHireIframe />
                {/* Render text after the widget, if any */}
                {afterWidget && (
                  <Box p={3} pt={beforeWidget ? 2 : 3}>
                    <Text
                      color="black"
                      mb={0}
                      sx={{
                        a: { color: "blue.500", textDecoration: "underline" },
                        p: {
                          whiteSpace: "pre-line",
                          marginBottom: "12px",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        },
                        "p:last-child": { marginBottom: 0 },
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          transformHtml(markdownToHtml(afterWidget)),
                          { ADD_ATTR: ["target", "rel"] }
                        ),
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          }
          
        }
        
        return <CarHireIframe key={`w-${idx}`} />;
      }

      /* Expedia widget: render both text and widget if both are present */
      const egDivRegex = /<div[^>]+class=["']eg-widget["'][^>]*><\/div>/i;
      const egDivMatch = message.match(egDivRegex);
      if (egDivMatch) {
        // Split message into text before widget and the widget snippet
        const [beforeWidget, ...afterWidget] = message.split(egDivRegex);
        const widgetSnippet = egDivMatch[0] + (message.includes('<script') ? message.slice(message.indexOf('<script')) : '');
        return (
          <>
            {/* Render text before the widget, if any */}
            {beforeWidget && beforeWidget.trim() && (
              <Box
                key={`txt-${idx}`}
                maxW={{ base: "90%", md: "65%", lg: "65%" }}
                p={3}
                borderRadius="xl"
                bg="#F6F6F6"
              >
                <Text
                  color="black"
                  mb={0}
                  sx={{
                    a: { color: "blue.500", textDecoration: "underline" },
                    p: {
                      whiteSpace: "pre-line",
                      marginBottom: "12px",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                    },
                    "p:last-child": { marginBottom: 0 },
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      transformHtml(markdownToHtml(beforeWidget)),
                      { ADD_ATTR: ["target", "rel"] }
                    ),
                  }}
                />
              </Box>
            )}
            {/* Render the Expedia widget */}
            <ExpediaSearchWidget key={`eg-${idx}`} snippet={widgetSnippet} fromChat={true} />
          </>
        );
      }

      /* Otherwise treat as normal text bubble */
      const safeHtml = DOMPurify.sanitize(
        transformHtml(markdownToHtml(message)),
        { ADD_ATTR: ["target", "rel"] }
      );

      return (
        <Box
          key={`txt-${idx}`}
          maxW={{ base: "90%", md: "65%", lg: "65%" }}
          p={3}
          borderRadius="xl"
          bg="#F6F6F6"
        >
          <Text
            color="black"
            mb={0}
            sx={{
              a: { color: "blue.500", textDecoration: "underline" },
              p: {
                whiteSpace: "pre-line",
                marginBottom: "12px",
                wordBreak: "break-word",
                overflowWrap: "anywhere",
              },
              "p:last-child": { marginBottom: 0 },
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        </Box>
      );
    }
  }
};

/* ──────────────────────────────────────────────────────────────────────────────── */

const MessageList = ({
  messages,
  handleSelectItem,
  isLoading,
  backgroundImage,
  conversationId,
}) => (
  <VStack spacing={4} align="stretch" w="100%">
    {messages.length ? (
      messages.map((msg, i) => (
        <VStack key={i} spacing={2} w="100%" alignItems="flex-start">
          {/* user bubble */}
          {msg.user_message && (
            <Box
              w={{ base: "90%", md: "65%", lg: "65%" }}
              p={3}
              mb={2}
              borderRadius="xl"
              alignSelf="flex-end"
              bg="#1A202C"
            >
              <Text color="white" mb={0}>
                {msg.user_message}
              </Text>
            </Box>
          )}

          {/* bot bubble(s) */}
          <Box w="100%" alignSelf="flex-start">
            {renderBotContent(
              msg.bot_message,
              i,
              handleSelectItem,
              conversationId
            )}

            {/* avatar only after the LAST bot reply */}
            {i === messages.length - 1 && !isLoading && (
              <Image
                src={backgroundImage}
                alt="Bot"
                boxSize="30px"
                borderRadius="full"
                border="2px solid"
                borderColor="#1A202C"
                ml={2}
                mt={2}
                objectFit="cover"
              />
            )}
          </Box>
        </VStack>
      ))
    ) : !isLoading ? (
      <Text ml={2} color="gray.400" w="80%">
        <Translate text="Start planning your trip here. Begin with the 'General' tab to unlock the other steps." />
      </Text>
    ) : null}

    {/* Ava typing … */}
    {isLoading && (
      <Box w="100%" p={2} borderRadius="md" alignSelf="flex-start">
        <Text color="gray.400" mb={0}>
          <Translate text="Ava is typing" />{" "}
          <Text
            as="span"
            _after={{
              content: `'.'`,
              animation: `${typingKeyframes} 1.5s infinite`,
            }}
          />
        </Text>
      </Box>
    )}
  </VStack>
);

export default MessageList;
